import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { BadgeCheck, RefreshCw, ShieldCheck, Star } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { logClientError } from '@/utils/clientError';
import { usePublicSeo } from '@/utils/seo';
import { formatBadgeLabel } from '@/utils/displayLabels';

const DEFAULT_RATING_DISTRIBUTION = {
  '5': 0,
  '4': 0,
  '3': 0,
  '2': 0,
  '1': 0,
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const VendorReviewPage = () => {
  const { vendorId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [reviewsPayload, setReviewsPayload] = useState({
    average_rating: 0,
    total_reviews: 0,
    rating_distribution: DEFAULT_RATING_DISTRIBUTION,
    items: [],
  });
  const [form, setForm] = useState({
    deliveryReference: '',
    reviewerName: '',
    rating: 0,
    comment: '',
  });

  usePublicSeo({
    title: vendor?.vendor_name ? `Laisser un avis à ${vendor.vendor_name} | Coli Noki` : 'Laisser un avis | Coli Noki',
    description: vendor?.vendor_name
      ? `Partagez votre expérience de livraison avec ${vendor.vendor_name} sur Coli Noki.`
      : 'Partagez votre expérience de livraison sur Coli Noki.',
    path: `/vendors/${vendorId}/review`,
  });

  const loadVendorReviewData = async ({ silent = false, suppressErrorToast = false, keepCurrentOnError = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [vendorResponse, reviewsResponse] = await Promise.all([
        axios.get(`${API}/vendors/${vendorId}`),
        axios.get(`${API}/vendors/${vendorId}/reviews`, {
          params: { page: 1, page_size: 8 },
        }),
      ]);

      setVendor(vendorResponse.data || null);
      setReviewsPayload({
        average_rating: reviewsResponse.data.average_rating || 0,
        total_reviews: reviewsResponse.data.total_reviews || 0,
        rating_distribution: reviewsResponse.data.rating_distribution || DEFAULT_RATING_DISTRIBUTION,
        items: reviewsResponse.data.items || [],
      });
    } catch (error) {
      logClientError('vendor_review.load_page', error);
      if (!suppressErrorToast) {
        toast.error(error.response?.data?.detail || "Impossible de charger la page d'avis");
      }
      if (!keepCurrentOnError) {
        setVendor(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadVendorReviewData();
  }, [vendorId]);

  const sellerBadges = useMemo(() => {
    const rawBadges = vendor?.seller_badges || vendor?.badges || [];
    const seen = new Set();
    return rawBadges.filter((badge) => {
      const badgeType = badge?.badge_type || badge;
      if (!badgeType) {
        return false;
      }
      if (vendor?.verified_status && badgeType === 'verified_seller') {
        return false;
      }
      if (vendor?.pro_status && badgeType === 'pro_seller') {
        return false;
      }
      if (seen.has(badgeType)) {
        return false;
      }
      seen.add(badgeType);
      return true;
    });
  }, [vendor]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    if (!form.rating || form.rating < 1 || form.rating > 5) {
      toast.error('Choisissez une note entre 1 et 5 étoiles.');
      return;
    }
    if (!String(form.deliveryReference || '').trim()) {
      toast.error('La référence de livraison est obligatoire.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        vendor_id: vendorId,
        delivery_reference: form.deliveryReference,
        reviewer_name: form.reviewerName || undefined,
        rating: form.rating,
        comment: form.comment || undefined,
      });

      toast.success('Avis envoyé avec succès.');
      setForm({
        deliveryReference: '',
        reviewerName: '',
        rating: 0,
        comment: '',
      });
      await loadVendorReviewData({
        silent: true,
        suppressErrorToast: true,
        keepCurrentOnError: true,
      });
    } catch (error) {
      logClientError('vendor_review.submit', error);
      toast.error(error.response?.data?.detail || "Impossible d'envoyer l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Header />
      <main className="flex-1">
        <div className="container mx-auto space-y-6 px-4 py-8">
          {loading ? (
            <div className="py-16 text-center text-slate-500">Chargement du vendeur...</div>
          ) : !vendor ? (
            <div className="py-16 text-center text-slate-500">Vendeur introuvable.</div>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <CardTitle className="text-2xl">{vendor.vendor_name || 'Vendeur Coli Noki'}</CardTitle>
                      <CardDescription>
                        Laissez un avis après une livraison terminée. Munissez-vous de votre référence de livraison.
                      </CardDescription>
                      <div className="flex flex-wrap gap-2">
                        {vendor.verified_status && (
                          <Badge className="bg-green-100 text-green-700">
                            <BadgeCheck className="mr-1 h-3 w-3" />
                            Vendeur vérifié
                          </Badge>
                        )}
                        {vendor.pro_status && <Badge className="bg-cyan-100 text-cyan-700">Coli Noki Pro</Badge>}
                        {sellerBadges.map((badge) => (
                          <Badge key={`${badge.badge_type || badge}-${badge.source || 'badge'}`} variant="outline">
                            {formatBadgeLabel(badge.badge_type || badge)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="min-w-[220px] rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-sm font-medium text-slate-500">Réputation actuelle</p>
                      <p className="mt-2 text-4xl font-semibold text-slate-900">
                        {Number(reviewsPayload.average_rating || 0).toFixed(2)}
                        <span className="ml-2 text-lg text-slate-500">/5</span>
                      </p>
                      <div className="mt-3 flex items-center gap-1 text-amber-500">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <Star
                            key={`review-star-${index}`}
                            className={`h-4 w-4 ${(index + 1) <= Math.round(Number(reviewsPayload.average_rating || 0)) ? 'fill-current' : 'text-slate-200'}`}
                          />
                        ))}
                      </div>
                      <p className="mt-2 text-sm text-slate-500">{reviewsPayload.total_reviews || 0} avis client(s)</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-cyan-600" />
                      Formulaire d’avis
                    </CardTitle>
                    <CardDescription>
                      Référence de livraison obligatoire. Un seul avis est accepté par livraison terminée.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="deliveryReference">Référence de livraison</Label>
                        <Input
                          id="deliveryReference"
                          value={form.deliveryReference}
                          onChange={(event) => setForm((current) => ({ ...current, deliveryReference: event.target.value }))}
                          placeholder="Ex : CN123456 ou référence commande"
                          required
                          disabled={submitting}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Votre note</Label>
                        <div className="flex flex-wrap items-center gap-2">
                          {Array.from({ length: 5 }).map((_, index) => {
                            const starValue = index + 1;
                            const isActive = starValue <= form.rating;
                            return (
                              <button
                                key={`rating-${starValue}`}
                                type="button"
                                onClick={() => setForm((current) => ({ ...current, rating: starValue }))}
                                disabled={submitting}
                                className={`rounded-full border p-2 transition ${isActive ? 'border-amber-300 bg-amber-50 text-amber-500' : 'border-slate-200 text-slate-300 hover:border-amber-200 hover:text-amber-400'} ${submitting ? 'cursor-not-allowed opacity-70' : ''}`}
                              >
                                <Star className={`h-5 w-5 ${isActive ? 'fill-current' : ''}`} />
                              </button>
                            );
                          })}
                          <span className="text-sm text-slate-500">{form.rating > 0 ? `${form.rating}/5` : 'Choisissez une note'}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="reviewerName">Prénom ou nom (optionnel)</Label>
                        <Input
                          id="reviewerName"
                          value={form.reviewerName}
                          onChange={(event) => setForm((current) => ({ ...current, reviewerName: event.target.value }))}
                          placeholder="Ex : Aline"
                          disabled={submitting}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="reviewComment">Commentaire</Label>
                        <Textarea
                          id="reviewComment"
                          rows={5}
                          value={form.comment}
                          onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
                          placeholder="Partagez votre expérience de livraison"
                          disabled={submitting}
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <Button type="submit" disabled={submitting}>
                          {submitting ? 'Envoi en cours...' : "Envoyer l'avis"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => loadVendorReviewData({ silent: true })}
                          disabled={refreshing || submitting}
                        >
                          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                          Actualiser
                        </Button>
                        <Link to="/vendors" className="text-sm text-cyan-700 hover:underline">
                          Voir les vendeurs vérifiés
                        </Link>
                      </div>
                    </form>
                  </CardContent>
                </Card>

                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Aperçu des notes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {['5', '4', '3', '2', '1'].map((bucket) => {
                        const count = Number(reviewsPayload.rating_distribution?.[bucket] || 0);
                        const total = Number(reviewsPayload.total_reviews || 0);
                        const width = total > 0 ? `${Math.max(6, Math.round((count / total) * 100))}%` : '0%';
                        return (
                          <div key={`distribution-${bucket}`} className="flex items-center gap-3">
                            <div className="w-12 text-sm font-medium text-slate-700">{bucket}★</div>
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full rounded-full bg-amber-400" style={{ width }} />
                            </div>
                            <div className="w-10 text-right text-sm text-slate-500">{count}</div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Avis récents</CardTitle>
                      <CardDescription>{reviewsPayload.total_reviews || 0} avis déjà publiés</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {reviewsPayload.items.length === 0 ? (
                        <p className="text-sm text-slate-500">Aucun avis disponible pour le moment.</p>
                      ) : (
                        reviewsPayload.items.map((review) => (
                          <div key={review.id} className="rounded-lg border border-slate-200 p-3">
                            <div className="flex items-center justify-between gap-3 text-sm">
                              <div className="font-medium text-slate-900">{review.reviewer_name || 'Client anonyme'}</div>
                              <div className="text-slate-500">{formatDate(review.created_at)}</div>
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-amber-500">
                              {Array.from({ length: 5 }).map((_, index) => (
                                <Star
                                  key={`${review.id}-star-${index}`}
                                  className={`h-4 w-4 ${(index + 1) <= Math.round(Number(review.rating || 0)) ? 'fill-current' : 'text-slate-200'}`}
                                />
                              ))}
                              <span className="ml-2 text-sm text-slate-600">{review.rating}/5</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-700">{review.comment || 'Sans commentaire'}</p>
                            {review.delivery_reference && (
                              <p className="mt-2 text-xs text-slate-500">Référence : {review.delivery_reference}</p>
                            )}
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <FooterNew />
    </div>
  );
};

export default VendorReviewPage;
